import * as React from 'react';
import { FormPanel, SubmitButton, Select, Spinner, Alert, Modal, Input } from '@ixicare/ui';
import { t } from '~/i18n';
import { getDefaultStateReducer } from '~/utils/reducer';
import { AlarmResolveVerificationMode, Site, SiteConfiguration } from '@ixicare/resources';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStores } from '~store/utils';
import { UserAction } from '~config/notification';
import { useSite } from '~hooks/site';
import GeoJSON from 'geojson';

// Pass `siteUuid` to target a specific site (/sites/<uuid>/configuration).
// Pass `null` to indicate "explicit mode but site not selected yet" — query stays disabled.
// Omit to use the legacy profile-scoped /sites/configuration endpoints.
export function useSiteConfiguration(siteUuid?: string | null) {
  const { notifications } = useStores();
  const queryClient = useQueryClient();
  const isExplicit = siteUuid !== undefined;
  const queryKey = isExplicit ? (['siteConfiguration', siteUuid] as const) : (['siteConfiguration'] as const);
  const query = useQuery({
    queryKey,
    queryFn: () =>
      typeof siteUuid === 'string' ? Site.getSiteConfigurationBySiteId(siteUuid) : Site.getSiteConfiguration(),
    enabled: !isExplicit || typeof siteUuid === 'string',
    retry: false,
    refetchOnWindowFocus: false,
    // Config rarely changes and the sole editor (ConfigurationForm) updates this
    // cache on save, so all readers can reuse it instead of refetching per page.
    staleTime: 60 * 1000,
  });
  const patchConfigMutation = useMutation({
    mutationFn: (body: SiteConfiguration) =>
      typeof siteUuid === 'string'
        ? Site.patchSiteConfigurationBySiteId(siteUuid, body)
        : Site.patchSiteConfiguration(body),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      notifications.addUserActionNotification({
        type: UserAction.SiteConfigurationUpdate,
        notificationType: 'success',
        message: t('site-configuration.update.success'),
      });
    },
  });
  return {
    mutate: patchConfigMutation.mutate,
    isMutating: patchConfigMutation.isPending,
    isLoading: query.isLoading,
    data: query.data,
  };
}

// Alarm-resolve verification is "on" for any non-"none" mode.
export function deviceVerificationEnabled(siteConfig?: SiteConfiguration | null): boolean {
  return (siteConfig?.['alarm-resolve-verification-mode'] ?? 'none') !== 'none';
}

export const ConfigurationForm: React.FC = observer(() => {
  const { sites, permissions } = useStores();
  const selectedSite = sites.selectedSite;
  const configurationQuery = useSiteConfiguration(selectedSite?.uuid ?? null);
  const configurationPermission = permissions.has('can_edit_site_configuration');

  return configurationPermission ? (
    <FormPanel>
      {!configurationQuery.isLoading && selectedSite ? (
        <Form
          key={selectedSite.uuid}
          siteUuid={selectedSite.uuid}
          isSubmitting={configurationQuery.isMutating}
          onSubmit={configurationQuery.mutate}
          configuration={configurationQuery.data}
        />
      ) : (
        <Spinner />
      )}
    </FormPanel>
  ) : (
    <p>{t('user-configuration.no-permissions')}</p>
  );
});

function Form(props: {
  onSubmit: (data: SiteConfiguration) => void;
  configuration?: SiteConfiguration;
  isSubmitting: boolean;
  siteUuid: string;
}) {
  const [state, dispatch] = React.useReducer(getDefaultStateReducer<SiteConfiguration>(), {
    ...props.configuration,
    'escalation-time-seconds': props.configuration?.['escalation-time-seconds'] ?? 300,
  });
  const [selectedTimezone, setSelectedTimezone] = React.useState<any>(props.configuration?.timezone ?? {});

  // Toggle to show/hide internal settings (segmentation & escalation)
  const showInternalSettings = import.meta.env.VITE_RESOURCE_ENV && import.meta.env.VITE_RESOURCE_ENV !== 'prod';
  const showSegmentationSettings = state['show-segmentation-settings'] ?? false;

  const handleTimeZoneChange = (time: any) => {
    const value = time.value;
    dispatch({ field: 'timezone', value });
    setSelectedTimezone(time);
  };

  const handleSubmit = React.useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      props.onSubmit(state);
    },
    [state]
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-12 col-md-6">
          <Select
            value={state['alarm-resolve-verification-mode']}
            onChange={(value) => dispatch({ field: 'alarm-resolve-verification-mode', value })}
            label={t('site-configuration.alarm-resolve-verification-mode.label')}
            options={Object.values(AlarmResolveVerificationMode).map((v) => ({
              value: v,
              label: t(`site-configuration.alarm-resolve-verification-mode.${v}`),
            }))}
          />
        </div>
        <div className="col-12 col-md-6 mt-30">
          <Alert type={'message'}>
            {t('site-configuration.alarm-resolve-verification-mode.explanation', {
              label: t('site-configuration.alarm-resolve-verification-mode.label'),
            })}
          </Alert>
        </div>
      </div>
      {state['alarm-resolve-verification-mode'] === 'device-verification' && (
        <div className="row">
          <div className="col-12 col-md-6">
            <Select
              value={state['require-proximity-control']}
              onChange={(value) => dispatch({ field: 'require-proximity-control', value })}
              label={t('site-configuration.alarm-resolve-verification-mode.required')}
              options={[
                { value: true, label: 'Enabled' },
                { value: false, label: 'Disabled' },
              ]}
            />
          </div>
          <div className="col-12 col-md-6 mt-30">
            <Alert type={'message'}>
              {t('site-configuration.alarm-resolve-verification-mode-required.explanation', {
                label: t('site-configuration.alarm-resolve-verification-mode.required'),
              })}
            </Alert>
          </div>
        </div>
      )}
      <div className="row">
        <div className="col-12 col-md-6">
          {props.siteUuid && (
            <OutdoorGeofenceInput
              siteUuid={props.siteUuid}
              value={state['default-outdoor-geofencing-geo']}
              onChange={(value) => dispatch({ field: 'default-outdoor-geofencing-geo', value })}
            />
          )}
        </div>
        <div className="col-12 col-md-6 mt-30">
          <Alert type={'message'}>{t('site-configuration.default-outdoor-geofencing-geo.explanation')}</Alert>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-6">
          <Select
            value={
              state['enable-battery-alerts-at-night'] === undefined ? false : state['enable-battery-alerts-at-night']
            }
            onChange={(value) => dispatch({ field: 'enable-battery-alerts-at-night', value })}
            label={t('site-configuration.battery-alerts-at-night')}
            options={[
              { value: true, label: 'Enabled' },
              { value: false, label: 'Disabled' },
            ]}
          />
        </div>
        <div className="col-12 col-md-6 mt-30">
          <Alert type={'message'}>{t('site-configuration.battery-alerts-at-night-description')}</Alert>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-6">
          <TimezoneSelector
            timezone={selectedTimezone}
            setTimezone={handleTimeZoneChange}
            label={t('site-configuration.timezone')}
            required={false}
          />
        </div>
        <div className="col-12 col-md-6 mt-30">
          <Alert type={'message'}>{t('site-configuration.timezone-description')}</Alert>
        </div>
      </div>

      <>
        <SectionDivider />
        <SectionTitle>{t('site-configuration.segmentation.title')}</SectionTitle>
        {!(showInternalSettings || showSegmentationSettings) && (
          <div className="col-12 col-md-12 ">
            <Alert type={'message'}>{t('site-configuration.segmentation.show-ui-disabled-description')}</Alert>
          </div>
        )}
        {(showInternalSettings || showSegmentationSettings) && (
          <>
            <div className="row">
              <div className="col-12 col-md-6">
                <Select
                  value={state['enable-segmentation'] === undefined ? false : state['enable-segmentation']}
                  onChange={(value) => dispatch({ field: 'enable-segmentation', value })}
                  label={t('site-configuration.enable-segmentation.label')}
                  options={[
                    { value: true, label: t('global.enabled') },
                    { value: false, label: t('global.disabled') },
                  ]}
                />
              </div>
              <div className="col-12 col-md-6 mt-30">
                <Alert type={'message'}>{t('site-configuration.enable-segmentation.description')}</Alert>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <Select
                  value={
                    state['enable-hcp-self-assign-hcp-group'] === undefined
                      ? false
                      : state['enable-hcp-self-assign-hcp-group']
                  }
                  onChange={(value) => dispatch({ field: 'enable-hcp-self-assign-hcp-group', value })}
                  label={t('site-configuration.enable-hcp-self-assign-hcp-group.label')}
                  options={[
                    { value: true, label: t('global.enabled') },
                    { value: false, label: t('global.disabled') },
                  ]}
                />
              </div>
              <div className="col-12 col-md-6 mt-30">
                <Alert type={'message'}>{t('site-configuration.enable-hcp-self-assign-hcp-group.description')}</Alert>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <Select
                  value={
                    state['disable-segmentation-for-geofence-alarms'] === undefined
                      ? false
                      : state['disable-segmentation-for-geofence-alarms']
                  }
                  onChange={(value) => dispatch({ field: 'disable-segmentation-for-geofence-alarms', value })}
                  label={t('site-configuration.disable-segmentation-for-geofence-alarms.label')}
                  options={[
                    { value: true, label: t('global.enabled') },
                    { value: false, label: t('global.disabled') },
                  ]}
                />
              </div>
              <div className="col-12 col-md-6 mt-30">
                <Alert type={'message'}>
                  {t('site-configuration.disable-segmentation-for-geofence-alarms.description')}
                </Alert>
              </div>
            </div>

            <SectionDivider />
            <SectionTitle>{t('site-configuration.escalation.title')}</SectionTitle>

            <div className="row">
              <div className="col-12 col-md-6">
                <Select
                  value={
                    state['enable-escalation-time-based'] === undefined ? false : state['enable-escalation-time-based']
                  }
                  onChange={(value) => dispatch({ field: 'enable-escalation-time-based', value })}
                  label={t('site-configuration.enable-escalation-time-based.label')}
                  options={[
                    { value: true, label: t('global.enabled') },
                    { value: false, label: t('global.disabled') },
                  ]}
                />
              </div>
              <div className="col-12 col-md-6 mt-30">
                <Alert type={'message'}>{t('site-configuration.enable-escalation-time-based.description')}</Alert>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <Input
                  type="number"
                  value={state['escalation-time-seconds'] ?? ''}
                  onChange={(e) =>
                    dispatch({
                      field: 'escalation-time-seconds',
                      value: e.target.value === '' ? undefined : parseInt(e.target.value),
                    })
                  }
                  label={t('site-configuration.escalation-time-seconds.label')}
                  min={30}
                  required
                />
              </div>
              <div className="col-12 col-md-6 mt-30">
                <Alert type={'message'}>{t('site-configuration.escalation-time-seconds.description')}</Alert>
              </div>
            </div>
          </>
        )}
      </>

      <SubmitButton loading={props.isSubmitting}>{t('global.update')}</SubmitButton>
    </form>
  );
}

import styled from 'styled-components';
import OutdoorGeofenceEditor from '~components/OutdoorGeofenceEditor';
import { observer } from 'mobx-react';
import TimezoneSelector from '~components/TimezoneSelector/TimezoneSelector';

const InputContainer = styled.div`
  width: 100%;
`;

const InputLabel = styled.label`
  font-family: Poppins;
  font-size: 14px;
  font-weight: 600;

  display: block;

  margin-bottom: 10px;

  color: var(--midnight-blue);
`;

const Value = styled.p`
  display: block;

  width: 100%;
  padding: 10px 15px;

  color: var(--comet);
  border: solid 1px #dee6f2;
  border-radius: 4px;
  background-color: var(--white);
  font-family: Poppins;
  cursor: pointer;

  &:hover {
    border: solid 1px var(--turquoise);
    box-shadow: 0 5px 20px 0 rgba(14, 12, 69, 0.1);
  }
`;

const SectionDivider = styled.hr`
  margin: 40px 0 30px 0;
  border: none;
  border-top: 2px solid #dee6f2;
`;

const SectionTitle = styled.h3`
  font-family: Poppins;
  font-size: 18px;
  font-weight: 600;
  color: var(--midnight-blue);
  margin-bottom: 20px;
`;

function OutdoorGeofenceInput({
  siteUuid,
  value,
  onChange,
}: {
  siteUuid: string;
  value?: GeoJSON.Feature<GeoJSON.Polygon>;
  onChange: (geo?: GeoJSON.Feature<GeoJSON.Polygon>) => void;
}) {
  const [isEdit, setEdit] = React.useState(false);
  const site = useSite(siteUuid);
  return (
    <InputContainer>
      <InputLabel>{t('site-configuration.default-outdoor-geofencing-geo.label')}</InputLabel>
      <Value onClick={() => setEdit(true)}>
        {value
          ? t('site-configuration.default-outdoor-geofencing-geo.edit')
          : t('site-configuration.default-outdoor-geofencing-geo.empty')}
      </Value>

      {isEdit && site && (
        <Modal onClose={() => setEdit(false)} style={{ maxWidth: 900 }}>
          <OutdoorGeofenceEditor
            onCancel={() => setEdit(false)}
            onSave={(x) => {
              setEdit(false);
              onChange(x);
            }}
            lat={site.lat}
            long={site.long}
            polygon={value}
          />
        </Modal>
      )}
    </InputContainer>
  );
}
